/**
 * Display-only currency conversion: rewrites store-currency prices (e.g. "Rs.54,000.00")
 * into the visitor's country currency using Shopify's published daily rates.
 * Checkout is untouched, so the cart shows a note that payment is taken in the store currency.
 */
(() => {
  const configElement = document.getElementById('LpCurrencyConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }

  const currencyFor = Object.fromEntries(
    (config.map || '')
      .split(',')
      .map((pair) => pair.split(':').map((part) => part.trim().toUpperCase()))
      .filter(([country, currency]) => country && currency)
  );
  const symbolOf = (currency) =>
    new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value || currency;

  // The country list and header button show the store currency for every country; show the display currency instead
  const relabelSelector = () => {
    document.querySelectorAll('.localization-form__list-item[data-value]').forEach((item) => {
      const currency = currencyFor[item.dataset.value];
      const label = item.querySelector('.localization-form__currency');
      if (!currency || !label) return;
      const text = ` ${currency} ${symbolOf(currency)}`;
      if (label.textContent !== text) label.textContent = text;
    });
    if (config.target) {
      document.querySelectorAll('.currency-code').forEach((code) => {
        if (code.textContent.trim() !== config.target) code.textContent = config.target;
      });
    }
  };

  const convertPrices = () => {
    const rates = window.Currency?.rates;
    const target = config.target;
    // Same currency in and out would rewrite each price with itself and re-trigger the observer forever
    if (!target || target === config.base || !rates?.[config.base] || !rates[target]) return;

    const factor = rates[config.base] / rates[target];
    const format = new Intl.NumberFormat(config.locale || undefined, { style: 'currency', currency: target });
    // Amounts written without decimals in content (e.g. "Rs 50,000") stay whole ("$181")
    const formatWhole = new Intl.NumberFormat(config.locale || undefined, {
      style: 'currency',
      currency: target,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const escaped = (config.symbol || config.base).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Also swallows a trailing currency code ("Rs.54,000.00 PKR") so it isn't left behind
    const pattern = new RegExp(`${escaped}\\.?\\s?(\\d[\\d,]*(?:\\.\\d+)?)(?:\\s?${config.base})?`, 'g');
    // The announcement bar states store-currency offers (e.g. free UK delivery over £150), so it stays as written
    const skip = 'SCRIPT, STYLE, TEXTAREA, INPUT, OPTION, [data-lp-no-convert], .localization-form, .lp-ann';

    const convertText = (text) =>
      text.replace(pattern, (_, amount) => {
        let value = Number(amount.replace(/,/g, '')) * factor;
        if (config.roundUp || !amount.includes('.')) value = Math.ceil(value);
        return (amount.includes('.') ? format : formatWhole).format(value);
      });

    // Writing only real changes means the observer never reacts to its own no-op writes
    const setText = (node) => {
      const next = convertText(node.nodeValue);
      if (next !== node.nodeValue) node.nodeValue = next;
    };

    const convertNode = (root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!pattern.test(node.nodeValue)) continue;
        pattern.lastIndex = 0;
        if (node.parentElement?.closest(skip)) continue;
        setText(node);
      }
      pattern.lastIndex = 0;
    };

    const addCartNotes = () => {
      if (!config.note) return;
      const text = config.note.replace('[currency]', target).replace('[base]', config.base);
      document.querySelectorAll('button[name="checkout"], .cart__checkout-button').forEach((button) => {
        const holder = button.closest('form') || button.parentElement;
        if (!holder || holder.querySelector('.lp-currency-note')) return;
        const note = document.createElement('p');
        note.className = 'lp-currency-note';
        note.setAttribute('data-lp-no-convert', '');
        note.textContent = text;
        holder.append(note);
      });
    };

    convertNode(document.body);
    addCartNotes();

    // Cart drawer, variant changes and quick views re-render prices after load
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          const node = mutation.target;
          if (pattern.test(node.nodeValue) && !node.parentElement?.closest(skip)) {
            pattern.lastIndex = 0;
            setText(node);
          }
          pattern.lastIndex = 0;
        } else {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              if (pattern.test(node.nodeValue) && !node.parentElement?.closest(skip)) setText(node);
              pattern.lastIndex = 0;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              convertNode(node);
            }
          });
        }
      }
      addCartNotes();
      relabelSelector();
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  relabelSelector();
  if (window.Currency?.rates) {
    convertPrices();
  } else {
    // currencies.js is deferred just before this file, so it has normally run already
    window.addEventListener('load', convertPrices, { once: true });
  }
})();
